// ─── L'area del maestro: un'altra applicazione, non un gestionale ridotto ────
//
// 🗣️ 18/09/2026: *«si deve vedere bene sul cellulare perché i maestri non usano
// un computer ma usano il cellulare»*.
//
// 🚨⭐⭐ **PERCHÉ UN GUSCIO SUO E NON `GRUPPI` FILTRATO.** Filtrare il menu non
// basterebbe, e il motivo è misurato: la barra laterale è `sticky` e larga
// 12.25rem, sotto i 1200 px diventa un cassetto col velo che **copre la
// pagina**, e la griglia del calendario a quattro campi è larga **520 px minimi**
// (`Griglia.tsx`: `4.5rem + N×min 7rem`) contro i 390 di un telefono — quindi
// scorre in orizzontale.
// ⇒ Il maestro non deve avere *meno voci*: deve avere **un'altra applicazione**,
//   con la navigazione dove sta il pollice e senza nessuna griglia da scorrere.
// 📌 *Una schermata pensata per un monitor largo non diventa un telefono
//    togliendole delle voci: diventa la stessa schermata con dei buchi.*
//
// ⚖️ E questo è l'unico pezzo **mobile-first** di un applicativo che è
// desktop-first e lo dichiara (`base.css`, § 03.8). Non è un'incoerenza ed è
// scritto qui perché non venga «riallineato al resto»: il resto lo usa la
// segreteria su uno schermo grande, questo lo usa un maestro a bordo campo.

import { useEffect, useLayoutEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { GIORNI, ORDINE_SETTIMANA, type BandaDelMioPerimetro, type IoMaestro } from '@pmo/shared';
import { chiama, esci } from '../../lib/api.js';
import { giornoPerEsteso } from '../../lib/time.js';
import { useSessione } from '../../features/auth/sessione.js';
import { Attesa, Guasto } from '../../components/pezzi.js';
import { Agenda } from './Agenda.js';
import { Lezione } from './Lezione.js';
import { Prenota, Sposta } from './Prenota.js';
import { ApriUnCorso, Corsi } from './Corsi.js';
import { Corso } from './Corso.js';
import { Soldi } from './Soldi.js';
import { PaginaAssenze, QuandoNonCiSono } from './NonCiSono.js';

export function AreaMaestro(): React.JSX.Element {
  /**
   * 🎾 Chi è **anche** qualcos'altro: il proprietario che insegna entra qui con
   * lo stesso account, e deve poter tornare di là. Si guarda il **ruolo** e non
   * il legame — un maestro e basta non ha nessun gestionale a cui tornare, e un
   * collegamento che lo porta a una schermata vietata è peggio di nessun
   * collegamento.
   */
  const { profilo } = useSessione();
  const haAncheIlGestionale = profilo !== null && profilo.role !== 'maestro';

  /**
   * 👁️🚨⭐⭐ **UNA SCHERMATA NUOVA COMINCIA DALLA SUA PRIMA RIGA — trovato
   *    guardando una fotografia il 20/09/2026.**
   *
   * 📏 Il difetto, misurato: l'agenda si apre **scorsa di seicento pixel** (è
   * la tappa ①: si apre sul confine fra il passato e oggi). Toccando una
   * lezione, quello scorrimento restava — la scheda si apriva a metà, e la riga
   * per tornare indietro finiva **sotto la testata appiccicata**: visibile per
   * un pezzo, e non premibile.
   * ⇒ Cambiando schermata si torna in cima.
   *
   * ⛔ **Tranne che sull'agenda, e non è un'eccezione di comodo**: quella lo
   *    scorrimento se lo decide da sé, e portarla in cima vorrebbe dire
   *    aprirla su sette giorni fa — cioè disfare la tappa ①. Qui si guarda il
   *    percorso perché è l'unica cosa che distingue «una schermata che si apre
   *    dove vuole lei» da tutte le altre.
   * ⚠️ È un `useLayoutEffect`: con un `useEffect` lo scorrimento si vedrebbe
   *    **saltare** dopo che la pagina è già comparsa.
   */
  const dove = useLocation().pathname;
  useLayoutEffect(() => {
    if (dove !== '/maestro') window.scrollTo(0, 0);
  }, [dove]);

  /**
   * ⭐ Il nome arriva dal SERVER, non dal profilo che sta già in memoria.
   *
   * Sembra un giro in più e non lo è: è la prova, a ogni caricamento, che la
   * porta si è aperta davvero — cioè che c'è una scheda `instructors` attiva
   * agganciata a questo accesso. Leggendolo dal profilo si vedrebbe il proprio
   * nome anche nell'istante in cui la segreteria mette a riposo il maestro, e
   * la prima cosa che non funzionerebbe sarebbe un bottone.
   */
  const io = useQuery({
    queryKey: ['maestro', 'io'],
    queryFn: () => chiama<IoMaestro>('GET', '/maestro/io'),
  });

  /**
   * 📏⭐ **QUANTO È ALTA LA TESTATA, scritto dove il CSS lo può leggere.**
   *
   * Il datario dell'agenda si appiccica **sotto** la testata, e per farlo deve
   * sapere quanto è alta. Non è un numero che si può scrivere a mano: cambia
   * col nome del maestro che va a capo, con la dimensione del carattere scelta
   * nel telefono, e con la tacca di un iPhone (`env(safe-area-inset-top)`).
   * ⇒ Si misura, e si scrive in una variabile che il foglio di stile usa per
   *   il `top` del datario e per `scroll-margin-top` dei giorni.
   * 📌 *Un'altezza indovinata è giusta su un telefono solo — di solito quello
   *    di chi l'ha scritta.*
   */
  const guscio = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const radice = guscio.current;
    const testata = radice?.querySelector('.testata-maestro');
    if (!radice || !testata) return;
    /* ⚠️ `getBoundingClientRect` e non `contentRect`: quello dell'osservatore è
       la scatola del **contenuto**, cioè senza i padding — e questa testata è
       fatta quasi tutta di padding (compreso quello della tacca dell'iPhone).
       Con `contentRect` il datario si appiccicherebbe una ventina di pixel
       troppo in alto, finendo sotto la testata. */
    const misura = new ResizeObserver(() => {
      const alta = testata.getBoundingClientRect().height;
      radice.style.setProperty('--testata-alta', `${Math.round(alta)}px`);
    });
    misura.observe(testata);
    return () => misura.disconnect();
  }, [io.data?.displayName]);

  return (
    <div className="area-maestro" ref={guscio}>
      <header className="testata-maestro">
        <div>
          <span className="tenue piccolo">Area maestro</span>
          <strong>{io.data?.displayName ?? '…'}</strong>
        </div>
        <div className="uscite">
          {haAncheIlGestionale && (
            <Link className="piatto" to="/calendario">
              Gestionale
            </Link>
          )}
          <button type="button" className="piatto" onClick={() => void esci()}>
            Esci
          </button>
        </div>
      </header>

      <main className={dove === '/maestro' ? 'pagina-maestro con-piu' : 'pagina-maestro'}>
        {io.isLoading ? (
          <Attesa cosa="la tua agenda" />
        ) : io.error ? (
          <Guasto errore={io.error} />
        ) : (
          <Routes>
            <Route path="/maestro" element={<Agenda />} />
            <Route path="/maestro/nuova" element={<Prenota />} />
            <Route path="/maestro/lezioni/:id" element={<Lezione />} />
            <Route path="/maestro/lezioni/:id/sposta" element={<Sposta />} />
            <Route path="/maestro/perimetro" element={<MioPerimetro />} />
            <Route path="/maestro/assenze" element={<PaginaAssenze />} />
            <Route path="/maestro/soldi" element={<Soldi />} />
            <Route path="/maestro/corsi" element={<Corsi />} />
            {/* ⚠️ «nuovo» PRIMA di «:id», o l'indirizzo della creazione verrebbe
                letto come l'identificativo di un corso che non esiste. */}
            <Route path="/maestro/corsi/nuovo" element={<ApriUnCorso />} />
            <Route path="/maestro/corsi/:id" element={<Corso />} />
            <Route path="*" element={<Navigate to="/maestro" replace />} />
          </Routes>
        )}
      </main>

      {/*
        🔄⭐⭐ **LA BARRA IN BASSO NON C'È PIÙ — voce 13, 23/09/2026.**
        🗣️ *«i tre bottoni, l'agenda, prenota e corsi, vanno levati, perché
        quando si apre l'app ti deve già essere sul calendario»* — e il mockup
        approvato del 20/09 l'aveva già disegnata così: l'agenda e un «+».
        ⇒ Si prenota dal «+» dell'agenda (⇒ `Agenda.tsx`), ai corsi si va dalla
          riga in fondo all'agenda, e ogni altra schermata ha il suo «‹» per
          tornare. Qui prima c'erano tre voci: L'agenda · Prenota · Corsi.
        📌 *Una barra che porta dove si è già non è navigazione: è una seconda
           strada per lo stesso posto, e occupa il fondo dello schermo — cioè il
           punto in cui sta il pollice.*
      */}
    </div>
  );
}

/**
 * 🎾⭐ Dove il circolo mi ha aperto.
 *
 * ⚖️ **Perché il maestro deve vederlo, e non scoprirlo da un rifiuto.** Il
 * perimetro lo decide la segreteria e lo subisce lui: se l'unico modo di
 * conoscerlo fosse provare a prenotare e sentirsi dire di no, imparerebbe le
 * regole del circolo sbagliando — e telefonerebbe ogni volta.
 * 📌 *Una regola che vincola qualcuno va mostrata a quel qualcuno, o non è una
 *    regola: è una sorpresa che si ripete.*
 *
 * ⚠️ E si dice per esteso anche quando è VUOTO. «Nessuna banda» non è una
 *    schermata che non ha caricato: è uno stato, e ha il suo rimedio scritto
 *    accanto — parlare con la segreteria, che è l'unica che può cambiarlo.
 *
 * 🏖️🔄⭐⭐ **E DAL 20/09 QUESTA SCHERMATA HA DUE SEZIONI, perché il perimetro
 * ha due metà scritte da due mani** (tappa ⑥): le **ore** le apre la segreteria
 * e lui le subisce; i **giorni in cui non c'è** li scrive lui — 🗣️ *«sì, si
 * blocca le ore da sé»*. ⇒ La riga qui sopra — *«il perimetro lo decide la
 * segreteria e lo subisce lui»* — resta vera della **prima** sezione, e non di
 * tutta la schermata.
 * ⚖️ Stanno insieme e non in due schermate perché leggerle separate vorrebbe
 *    dire guardare un elenco di ore senza sapere che tre di quei giorni sono
 *    già tolti.
 *
 * 🔄 **Dal 20/09 è una schermata sua, e prima stava in fondo alla giornata.**
 * Con un giorno solo a schermo ci si arrivava scorrendo un pezzo; con
 * quattordici giorni in fila sarebbe finita **dopo** tutti — cioè in un posto
 * in cui non passa nessuno. Ci si arrivava dalla riga in fondo all'agenda.
 * 🔄 Dal 23/09 (voce 16) quella riga non c'è più: le assenze hanno una pagina
 * loro (`/maestro/assenze`, dal «+»), e questo indirizzo resta vivo ma senza
 * nessun collegamento che ci porti — 🗣️ *«quando puoi prenotare non ci serve
 * più»*.
 * 📌 *Una cosa che sta «sotto» una lista lunga non è in fondo alla schermata:
 *    è fuori.*
 */
function MioPerimetro(): React.JSX.Element {
  const perimetro = useQuery({
    queryKey: ['maestro', 'perimetro'],
    queryFn: () => chiama<{ items: BandaDelMioPerimetro[] }>('GET', '/maestro/perimetro'),
  });

  if (perimetro.isLoading) return <Attesa cosa="le tue ore" />;
  if (perimetro.error) return <Guasto errore={perimetro.error} />;
  const items = perimetro.data?.items ?? [];

  return (
    <>
      <p>
        <Link className="indietro" to="/maestro">
          ‹ La mia agenda
        </Link>
      </p>

      <section className="riquadro">
        <h2>Quando puoi prenotare</h2>
        {items.length === 0 ? (
          <p className="tenue">
            Il circolo non ti ha ancora aperto nessuna ora. Finché non lo fa non puoi prenotare da te: lo
            decide la segreteria.
          </p>
        ) : (
          <ul className="elenco-bande">
            {ORDINE_SETTIMANA.filter((g) => items.some((b) => b.weekday === g)).map((g) => (
              <li key={g}>
                <strong>{GIORNI[g]}</strong>
                <ul>
                  {items
                    .filter((b) => b.weekday === g)
                    .map((b) => (
                      /* 🔑 La chiave è composta e non un `id`: una banda non ha un
                       identificativo, di qua — il maestro non la cita mai
                       (⇒ `shared/maestro.ts`). L'indice unico del database
                       garantisce che questa quaterna non si ripeta. */
                      <li key={`${b.courtId ?? 'tutti'}-${b.startsAtTime}-${b.endsAtTime}`}>
                        <span>{b.courtName ?? 'Qualunque campo'}</span>
                        <span>
                          {b.startsAtTime}–{b.endsAtTime}
                        </span>
                      </li>
                    ))}
                </ul>
              </li>
            ))}
            {/* 🎓 18/10/2026: i giorni solo, «solo quel giorno». */}
            {[...new Set(items.filter((b) => b.onDate !== null).map((b) => b.onDate!))].sort().map((d) => (
              <li key={d}>
                <strong>{giornoPerEsteso(d)}</strong>
                <ul>
                  {items
                    .filter((b) => b.onDate === d)
                    .map((b) => (
                      <li key={`${b.courtId ?? 'tutti'}-${b.startsAtTime}-${b.endsAtTime}`}>
                        <span>{b.courtName ?? 'Qualunque campo'}</span>
                        <span>
                          {b.startsAtTime}–{b.endsAtTime} · solo quel giorno
                        </span>
                      </li>
                    ))}
                </ul>
              </li>
            ))}
          </ul>
        )}
        {/* La riga che chiude il senso: dice cosa si può fare dentro quelle ore
            e chi fa il resto, così il perimetro non sembra un divieto senza
            porta d'uscita. */}
        <p className="tenue piccolo">Dentro queste ore prenoti da solo. Fuori, lo fa la segreteria.</p>
      </section>

      <QuandoNonCiSono />
    </>
  );
}
